import React, { Component } from "react";
import { Row, Col, Divider, message, Upload, Space, Tag } from "antd";
import {
  PictureOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import {
  Form,
  FormItem,
  InputItem,
  TextAreaItem,
  SwitchItem,
  ButtonItem,
  DynamicCardForm,
} from "../../../../Components/Items";

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

class PuntoForm extends Component {
  formRef = React.createRef();

  async getData() {
    let values;
    try {
      values = await this.formRef.current.validateFields();
    } catch {
      message.error("Revisa los campos requeridos del punto");
      return Promise.reject();
    }

    const pasos = (values.procedimiento?.pasos || []).map((paso, index) => ({
      orden: index + 1,
      descripcion: paso.descripcion,
    }));

    let imagenPuntoData = undefined;
    if (values.imagen && values.imagen.length > 0) {
      const fileItem = values.imagen[0];
      
      if (fileItem.url) {
         imagenPuntoData = { name: fileItem.name, url: fileItem.url };
      } 
      else if (fileItem.originFileObj && fileItem.originFileObj instanceof Blob) {
         try {
             const base64 = await getBase64(fileItem.originFileObj);
             imagenPuntoData = { name: fileItem.name, url: base64, originFileObj: fileItem.originFileObj };
         } catch (e) {
             console.error("Error leyendo archivo:", e);
             imagenPuntoData = { name: fileItem.name, url: "" }; 
         }
      } 
      else {
         imagenPuntoData = { name: fileItem.name || "imagen.png", url: "" };
      }
    }

    return {
      nombre: values.nombre || "",
      descripcion: values.descripcion || "",
      observaciones: values.observaciones || "",
      esPlantilla: values.esPlantilla || false,
      version: values.version || "",
      imagen: imagenPuntoData, 
      procedimiento: { pasos },
      criterios: values.criterios || [],
    };
  }

  render() {
    const { disabled, initialValues, camposCalidad } = this.props;
    const cp = camposCalidad?.punto ?? {};
    const showPasos               = cp.pasos               !== false;
    const showCriterioTolerancia  = cp.criterioTolerancia  !== false;
    const showCriterioReferencias = cp.criterioReferencias !== false;

    const cardStyle = {
      border: "1px solid #ebedf0",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      backgroundColor: "#ffffff",
      padding: "16px",
      width: "100%",
    };

    return (
      <Form ref={this.formRef} initialValues={initialValues}>
        <DynamicCardForm 
            title={
                <span>
                    Datos del Punto
                    {initialValues?.derivadoDe && (
                        <Tag color="cyan" style={{ marginLeft: 10 }}>
                            Derivado de plantilla
                        </Tag>
                    )}
                </span>
            } 
            style={cardStyle}
        >
          <Row gutter={16}>
            <Col span={12}>
              <FormItem
                label="Nombre"
                name="nombre"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                tooltip="Nombre identificador del punto de control"
              >
                <InputItem placeholder="Nombre del punto" disabled={disabled} />
              </FormItem>
            </Col>
            
            <Col span={8}>
              <FormItem
                label="Observaciones"
                name="observaciones"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputItem disabled={disabled} />
              </FormItem>
            </Col>

            <Col span={4}>
              <FormItem
                label="Es Plantilla"
                name="esPlantilla"
                valuePropName="checked"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                tooltip="Guarda este punto como plantilla reutilizable"
              >
                <SwitchItem disabled={disabled} />
              </FormItem>
            </Col>

            <FormItem noStyle shouldUpdate={(prev, curr) => prev.esPlantilla !== curr.esPlantilla}>
              {({ getFieldValue }) =>
                getFieldValue("esPlantilla") ? (
                  <Col span={12}>
                    <FormItem
                      label="Versión"
                      name="version"
                      rules={[{ required: true, message: "Ingresa la versión de la plantilla" }]}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                    >
                      <InputItem placeholder="Ej. 1.0" disabled={disabled} />
                    </FormItem>
                  </Col>
                ) : null
              }
            </FormItem>

            <Col span={24}>
              <FormItem
                label="Descripción"
                name="descripcion"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <TextAreaItem rows={2} disabled={disabled} />
              </FormItem>
            </Col>

            <Col span={24}>
                <FormItem
                    label="Imagen del Punto"
                    name="imagen"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                >
                    <Upload
                        listType="picture-card"
                        maxCount={1}
                        beforeUpload={() => false}
                        accept="image/*"
                        disabled={disabled}
                    >
                        <FormItem noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const fileList = getFieldValue("imagen") || [];
                                return fileList.length === 0 ? (
                                    <div style={{ fontSize: 11 }}>
                                        <PictureOutlined />
                                        <div>Subir Imagen</div>
                                    </div>
                                ) : null;
                            }}
                        </FormItem>
                    </Upload>
                </FormItem>
            </Col>
          </Row>
        </DynamicCardForm>

        {showPasos && <DynamicCardForm title="Pasos del Procedimiento" style={{ ...cardStyle, marginTop: 12 }}>
          <Form.List name={["procedimiento", "pasos"]}>
            {(pasosFields, { add: addPaso, remove: removePaso, move: movePaso }) => (
              <>
                {pasosFields.length > 0 && (
                  <Row gutter={8} style={{ marginBottom: 8, fontWeight: 600, fontSize: 12, color: "#666" }}>
                    <Col span={2} style={{ textAlign: "center" }}>#</Col>
                    <Col span={18}>Descripción del Paso</Col>
                    <Col span={4} style={{ textAlign: "center" }}>Acciones</Col>
                  </Row>
                )}
                
                {pasosFields.map((pasoField, pasoIdx) => (
                  <Row gutter={8} key={pasoField.key} style={{ marginBottom: 6 }} align="middle">
                    <Col span={2} style={{ textAlign: "center" }}>
                      <Tag color="blue">{pasoIdx + 1}</Tag>
                    </Col>
                    <Col span={18}>
                      <FormItem name={[pasoField.name, "descripcion"]} noStyle rules={[{ required: true, message: "Describe el paso" }]}>
                        <InputItem placeholder="Describe la acción a realizar..." disabled={disabled} />
                      </FormItem>
                    </Col>
                    
                    <Col span={4} style={{ display: "flex", justifyContent: "center" }}>
                      <Space size={4}>
                        <ButtonItem
                          size="small"
                          icon={<ArrowUpOutlined />}
                          disabled={pasoIdx === 0 || disabled}
                          onClick={() => movePaso(pasoIdx, pasoIdx - 1)}
                        />
                        <ButtonItem
                          size="small"
                          icon={<ArrowDownOutlined />}
                          disabled={pasoIdx === pasosFields.length - 1 || disabled}
                          onClick={() => movePaso(pasoIdx, pasoIdx + 1)}
                        />
                        <ButtonItem
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => removePaso(pasoField.name)}
                          disabled={disabled}
                        />
                      </Space>
                    </Col>
                  </Row>
                ))}
                
                {!disabled && (
                  <ButtonItem
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => addPaso({ descripcion: "" })}
                    style={{ marginTop: 8 }}
                  >
                    Añadir Paso al Procedimiento
                  </ButtonItem>
                )}
              </>
            )}
          </Form.List>
        </DynamicCardForm>}

        <DynamicCardForm
          title="Criterios de Aceptación"
          style={{ ...cardStyle, marginTop: 12 }}
        >
          <Form.List name="criterios">
            {(criteriosFields, { add: addCriterio, remove: removeCriterio }) => (
              <>
                {criteriosFields.map((criterioField) => (
                  <div
                    key={criterioField.key}
                    style={{
                      border: "1px solid #ebedf0",
                      borderRadius: 6,
                      padding: "10px 12px",
                      marginBottom: 10,
                    }}
                  >
                    <Row gutter={8} align="middle" style={{ marginBottom: 6 }}>
                      <Col span={showCriterioTolerancia ? 10 : 16}>
                        <FormItem
                          name={[criterioField.name, "criterio"]}
                          rules={[{ required: true, message: "Describe el criterio" }]}
                          tooltip="Descripción del criterio de aceptación"
                          style={{ marginBottom: 0 }}
                        >
                          <InputItem
                            placeholder="Descripción del criterio"
                            disabled={disabled}
                          />
                        </FormItem>
                      </Col>
                      <Col span={6}>
                        <FormItem
                          name={[criterioField.name, "tipo"]}
                          rules={[{ required: true, message: "Ingresa el tipo de criterio" }]}
                          tooltip="Tipo de criterio"
                          style={{ marginBottom: 0 }}
                        >
                          <InputItem placeholder="Tipo de criterio" disabled={disabled} />
                        </FormItem>
                      </Col>
                      {showCriterioTolerancia && (
                        <Col span={6}>
                          <FormItem
                            name={[criterioField.name, "tolerancia"]}
                            tooltip="Margen de error permitido, ej. 5%, 10 unidades"
                            style={{ marginBottom: 0 }}
                          >
                            <InputItem placeholder="Ej. 5%, 10 u." disabled={disabled} />
                          </FormItem>
                        </Col>
                      )}
                      <Col span={2} style={{ textAlign: "right" }}>
                        <ButtonItem
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => removeCriterio(criterioField.name)}
                          disabled={disabled}
                        />
                      </Col>
                    </Row>
                    {showCriterioReferencias && (
                    <><Divider orientation="left" style={{ fontSize: 11, margin: "6px 0" }}>
                      Referencias
                    </Divider>
                    <Form.List name={[criterioField.name, "referencias"]}>
                      {(refsFields, { add: addRef, remove: removeRef }) => (
                        <>
                          {refsFields.map((refField) => (
                            <Row
                              gutter={6}
                              key={refField.key}
                              style={{ marginBottom: 4 }}
                              align="middle"
                            >
                              <Col span={9}>
                                <FormItem name={[refField.name, "url"]} noStyle>
                                  <InputItem placeholder="URL" disabled={disabled} />
                                </FormItem>
                              </Col>
                              <Col span={7}>
                                <FormItem name={[refField.name, "titulo"]} noStyle>
                                  <InputItem placeholder="Título" disabled={disabled} />
                                </FormItem>
                              </Col>
                              <Col span={6}>
                                <FormItem name={[refField.name, "tipo"]} noStyle>
                                  <InputItem placeholder="Tipo ref." disabled={disabled} />
                                </FormItem>
                              </Col>
                              <Col span={2}>
                                <ButtonItem
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeRef(refField.name)}
                                  disabled={disabled}
                                />
                              </Col>
                            </Row>
                          ))}
                          {!disabled && (
                            <ButtonItem
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => addRef({ url: "", titulo: "", tipo: "" })}
                              style={{ marginTop: 4 }}
                            >
                              Agregar Referencia
                            </ButtonItem>
                          )}
                        </>
                      )}
                    </Form.List></>)}
                  </div>
                ))}
                {!disabled && (
                  <ButtonItem
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      addCriterio({ criterio: "", tipo: "", tolerancia: "", referencias: [] })
                    }
                  >
                    Agregar Criterio
                  </ButtonItem>
                )}
              </>
            )}
          </Form.List>
        </DynamicCardForm>
      </Form>
    );
  }
}

export default PuntoForm;